import React from "react";
import { useParams, Link } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Breadcrumbs,
    Link as MuiLink,
} from "@mui/material";
import { news } from "./NewsData";
import AdSense from "../../components/AdSense";
import SEO from "../../components/SEO/SEO";
import AppDownloadCTA from "../../components/AppDownloadCTA/AppDownloadCTA";
import { SITE_NAME, SITE_URL } from "../../seo/constants";

export default function NewsPage() {
    const { slug } = useParams();
    const article = news[slug];

    if (!article) {
        return (
            <Box sx={{ p: 5, textAlign: "center" }}>
                <SEO
                    title="News not found"
                    description="Article not found."
                    path={`/news/${slug || ""}`}
                    noindex
                />
                <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
                    News not found
                </Typography>
                <Button component={Link} to="/news" variant="contained">
                    Back to News
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                maxWidth: 1260,
                mx: "auto",
                px: 2,
                py: { xs: 3, md: 5 },
                background:
                    "linear-gradient(180deg, rgba(240,248,240,0.45) 0%, #ffffff 28%)",
            }}
        >
            <SEO
                title={`${article.title} | ${SITE_NAME}`}
                description={article.paragraph || article.title}
                path={`/news/${slug}`}
                image={
                    article.image && article.image !== "None"
                        ? article.image
                        : undefined
                }
                type="article"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "NewsArticle",
                    headline: article.title,
                    description: article.paragraph || article.title,
                    image:
                        article.image && article.image !== "None"
                            ? article.image
                            : `${SITE_URL}/images/og-cover.png`,
                    author: { "@type": "Organization", name: SITE_NAME },
                }}
            />

            <Box
                className="side-ad"
                sx={{
                    width: 160,
                    flexShrink: 0,
                    display: { xs: "none", lg: "block" },
                }}
            >
                <AdSense slot="9786570260" />
            </Box>

            <Box sx={{ maxWidth: 900, width: "100%" }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <MuiLink component={Link} to="/" underline="hover" color="inherit">
                        Home
                    </MuiLink>
                    <MuiLink
                        component={Link}
                        to="/news"
                        underline="hover"
                        color="inherit"
                    >
                        News
                    </MuiLink>
                    <Typography color="text.primary">Article</Typography>
                </Breadcrumbs>

                <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                        fontSize: { xs: "1.75rem", md: "2.25rem" },
                        fontWeight: 700,
                        mb: 3,
                        lineHeight: 1.25,
                    }}
                >
                    {article.title}
                </Typography>

                {article.image && article.image !== "None" && (
                    <Box
                        component="img"
                        src={article.image}
                        alt={article.title}
                        sx={{
                            width: "100%",
                            maxHeight: 480,
                            objectFit: "cover",
                            borderRadius: 3,
                            mb: 3,
                        }}
                    />
                )}

                <Typography
                    variant="body1"
                    sx={{ fontSize: "1.1rem", lineHeight: 1.85, mb: 3 }}
                >
                    {article.paragraph}
                </Typography>

                <Box sx={{ my: 3 }}>
                    <AdSense slot="2566597353" />
                </Box>

                {article.link && (
                    <Button
                        variant="contained"
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mb: 2 }}
                    >
                        Read Full Article →
                    </Button>
                )}

                <AppDownloadCTA
                    title="Stay informed — and stay connected to the Quran"
                    subtitle="Quran Chat Buddy helps Muslims with AI Quran guidance, Salah prayer times, Qibla, and daily ayat reminders."
                />
            </Box>

            <Box
                className="side-ad"
                sx={{
                    width: 160,
                    flexShrink: 0,
                    display: { xs: "none", lg: "block" },
                }}
            >
                <AdSense slot="9786570260" />
            </Box>
        </Box>
    );
}
